import { useContext, useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AuthContext from '../../context/authContext';
import useEmployeeDiscountCode from '../../hooks/useEmployeeDiscountCode';
import LockIconWrapper from '../components/LockIconWrapper';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const WEEKDAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const PERIOD_LABELS = { week: 'Woche', month: 'Monat', quarter: 'Quartal', year: 'Jahr' };

const STATUS_CHIP = {
  active: { label: 'Aktiv', color: 'success' },
  expired: { label: 'Abgelaufen', color: 'default' },
  redeemed: { label: 'Eingelöst', color: 'info' },
  revoked: { label: 'Widerrufen', color: 'error' },
};

function EmployeeDiscountCodes() {
  const authCtx = useContext(AuthContext);
  const { isLoading, getDrawableDiscountCodes, drawIndividualCode, getMyDrawnCodes } =
    useEmployeeDiscountCode();

  const [drawableCodes, setDrawableCodes] = useState([]);
  const [expandedCodeId, setExpandedCodeId] = useState(null);
  const [drawnCodesMap, setDrawnCodesMap] = useState({});
  const [drawingId, setDrawingId] = useState(null);

  if (!authCtx.checkRight('discountCode:read:own')) {
    return <Navigate to='/welcome' replace />;
  }

  const loadDrawable = useCallback(async () => {
    try {
      const data = await getDrawableDiscountCodes();
      if (Array.isArray(data)) {
        setDrawableCodes(data);
      }
    } catch (e) {
      toast.error('Fehler beim Laden der verfügbaren Rabattcodes');
    }
  }, []);

  useEffect(() => {
    loadDrawable();
  }, []);

  const handleExpand = async discountCodeId => {
    if (expandedCodeId === discountCodeId) {
      setExpandedCodeId(null);
      return;
    }
    setExpandedCodeId(discountCodeId);
    // Load drawn codes for this discount code
    try {
      const codes = await getMyDrawnCodes(discountCodeId);
      if (Array.isArray(codes)) {
        setDrawnCodesMap(prev => ({ ...prev, [discountCodeId]: codes }));
      }
    } catch (e) {
      toast.error('Fehler beim Laden der gezogenen Codes');
    }
  };

  const handleDraw = async discountCodeId => {
    setDrawingId(discountCodeId);
    try {
      const result = await drawIndividualCode(discountCodeId);
      if (result?.code) {
        toast.success(`Code gezogen: ${result.code}`);
        // Refresh drawn codes and drawable list
        const [codes] = await Promise.all([getMyDrawnCodes(discountCodeId), loadDrawable()]);
        if (Array.isArray(codes)) {
          setDrawnCodesMap(prev => ({ ...prev, [discountCodeId]: codes }));
        }
        setExpandedCodeId(discountCodeId);
      }
    } catch (e) {
      const msg = e?.message || 'Fehler beim Ziehen des Codes';
      toast.error(msg);
    } finally {
      setDrawingId(null);
    }
  };

  const copyToClipboard = code => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success('Code in Zwischenablage kopiert');
    });
  };

  const formatAvailabilityWindows = windows => {
    if (!windows || windows.length === 0) return 'Keine Einschränkung';
    return windows
      .map(w => {
        const days = (w.dayCode || []).map(d => WEEKDAY_LABELS[d] || d).join(', ');
        return `${days}: ${w.start}–${w.end}`;
      })
      .join(' | ');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' gutterBottom>
        Meine Rabattcodes
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Hier kannst du persönliche Rabattcodes ziehen, die du bei Buchungen einlösen kannst.
      </Typography>

      {isLoading && drawableCodes.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && drawableCodes.length === 0 && (
        <Alert severity='info'>Aktuell stehen dir keine Rabattcodes zur Verfügung.</Alert>
      )}

      {drawableCodes.map(dc => {
        const limitReached = dc.drawLimit && dc.drawsInPeriod >= dc.drawLimit;
        const drawnCodes = drawnCodesMap[dc._id] || [];
        const isExpanded = expandedCodeId === dc._id;

        return (
          <Card key={dc._id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant='h6'>{dc.description || 'Rabattcode'}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Rabatt: {dc.globalPercent}%
                  </Typography>
                  {dc.availabilityWindows && dc.availabilityWindows.length > 0 && (
                    <Typography variant='body2' color='text.secondary'>
                      Gültig: {formatAvailabilityWindows(dc.availabilityWindows)}
                    </Typography>
                  )}
                  {dc.maxParticipants && (
                    <Typography variant='body2' color='text.secondary'>
                      Max. {dc.maxParticipants} Teilnehmer
                    </Typography>
                  )}
                  {dc.individualCodeExpiryDays && (
                    <Typography variant='body2' color='text.secondary'>
                      Code gültig für {dc.individualCodeExpiryDays} Tage nach Ziehung
                    </Typography>
                  )}
                </Box>
                {dc.drawLimit && (
                  <Chip
                    label={`${dc.drawsInPeriod} / ${dc.drawLimit} pro ${PERIOD_LABELS[dc.drawLimitPeriod] || dc.drawLimitPeriod}`}
                    color={limitReached ? 'error' : 'default'}
                    size='small'
                  />
                )}
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
              <Button
                variant='contained'
                size='small'
                disabled={limitReached || drawingId === dc._id}
                onClick={() => handleDraw(dc._id)}
                startIcon={drawingId === dc._id ? <CircularProgress size={16} /> : null}
              >
                <LockIconWrapper right='discountCode:special:draw'>
                  {drawingId === dc._id ? 'Wird gezogen...' : 'Code ziehen'}
                </LockIconWrapper>
              </Button>
              <IconButton size='small' onClick={() => handleExpand(dc._id)}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </CardActions>
            <Collapse in={isExpanded}>
              <Box sx={{ px: 2, pb: 2 }}>
                {drawnCodes.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    Du hast noch keine Codes für diesen Rabattcode gezogen.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Code</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Gezogen am</TableCell>
                          <TableCell>Gültig bis</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {drawnCodes.map(code => {
                          const statusInfo = STATUS_CHIP[code.status] || STATUS_CHIP.active;
                          return (
                            <TableRow key={code._id}>
                              <TableCell>
                                <Typography variant='body2' fontFamily='monospace' fontWeight='bold'>
                                  {code.code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={statusInfo.label} color={statusInfo.color} size='small' />
                              </TableCell>
                              <TableCell>
                                {dayjs(code.createdAt).format('DD.MM.YYYY HH:mm')}
                              </TableCell>
                              <TableCell>
                                {dayjs(code.expiresAt).format('DD.MM.YYYY')}
                              </TableCell>
                              <TableCell>
                                {code.status === 'active' && (
                                  <IconButton size='small' onClick={() => copyToClipboard(code.code)} title='Code kopieren'>
                                    <ContentCopyIcon fontSize='small' />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Collapse>
          </Card>
        );
      })}
    </Box>
  );
}

export default EmployeeDiscountCodes;
